import "@theme/load-more";

/** Named view transition for filter-driven collection grid swaps (see utilities.css). */
const FILTER_GRID_VIEW_TRANSITION_NAME = "filter-collection-grid";

/** Applied to the grid mount while filter results are fetching; dims cards via utilities.css */
const FILTER_RESULTS_PENDING_CLASS = "filter-results-pending";

class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.rangeInputs = this.querySelectorAll('[range-input] input[type="range"]');
    this.range = this.querySelector("[slider] [progress-bar]");
    this.minInput = this.querySelector("[min-price-filter]");
    this.maxInput = this.querySelector("[max-price-filter]");
    this.priceInput = this.querySelectorAll("[price-input-show]");
    this.money_format =
      window.Theme?.localization?.currency?.money_format ||
      window.Shopify?.money_format ||
      (window.Theme?.localization?.currency?.symbol
        ? `${window.Theme.localization.currency.symbol}{{amount}}`
        : null) ||
      "£{{amount}}";
    const currencyCode =
      window.Theme?.localization?.currency?.iso_code ||
      window.Shopify?.currency?.active ||
      "";
    if (currencyCode && currencyCode !== "GBP" && !this.money_format.includes(currencyCode)) {
      this.money_format = `${this.money_format} ${currencyCode}`;
    }
    this.priceGap = 1;
  }

  connectedCallback() {
    if (this.rangeInputs.length !== 2 || !this.range || !this.minInput || !this.maxInput) {
      console.error("PriceRangeSlider: Missing required elements");
      return;
    }

    this.initializeSlider();
    this.attachEventListeners();
  }

  initializeSlider() {
    const rangeMax = Number(this.rangeInputs[1].max) || 1;
    const minVal = Number(this.rangeInputs[0].value) || 0;
    const maxVal = Number(this.rangeInputs[1].value) || rangeMax;
    this.range.style.left = (minVal / rangeMax) * 100 + "%";
    this.range.style.right = 100 - (maxVal / rangeMax) * 100 + "%";
  }

  attachEventListeners() {
    // Price input listeners
    this.priceInput.forEach((input) => {
      input.addEventListener("input", (e) => {
        let minPrice = parseInt(this.priceInput[0].getAttribute("data-term"));
        let maxPrice = parseInt(this.priceInput[1].getAttribute("data-term"));

        if (maxPrice - minPrice >= this.priceGap && maxPrice <= this.rangeInputs[1].max) {
          if (e.target.className === "input-min") {
            this.rangeInputs[0].value = minPrice;
            this.rangeInputs[0].setAttribute("data-term", minPrice);
            this.range.style.left = (minPrice / this.rangeInputs[0].max) * 100 + "%";
          } else {
            this.rangeInputs[1].value = maxPrice;
            this.maxInput.value = maxPrice;
            this.range.style.right = 100 - (maxPrice / this.rangeInputs[1].max) * 100 + "%";
          }
        }
      });
    });

    // Range input listeners
    this.rangeInputs.forEach((input) => {
      input.addEventListener("input", (e) => {
        let minVal = parseInt(this.rangeInputs[0].value);
        let maxVal = parseInt(this.rangeInputs[1].value);

        if (maxVal - minVal < this.priceGap) {
          if (e.target.className === "range-min") {
            this.rangeInputs[0].value = maxVal - this.priceGap;
          } else {
            this.rangeInputs[1].value = minVal + this.priceGap;
          }
        } else {
          this.priceInput[0].setAttribute("data-term", minVal);
          this.priceInput[1].setAttribute("data-term", maxVal);
          this.priceInput[0].innerHTML = Shopify.formatMoney(
            minVal * 100,
            this.money_format,
          ).replace(/[.,]00(?=(\s|$))/, "");
          this.priceInput[1].innerHTML = Shopify.formatMoney(
            maxVal * 100,
            this.money_format,
          ).replace(/[.,]00(?=(\s|$))/, "");
          this.range.style.left = (minVal / this.rangeInputs[0].max) * 100 + "%";
          this.range.style.right = 100 - (maxVal / this.rangeInputs[1].max) * 100 + "%";
          this.minInput.value = minVal;
          this.maxInput.value = maxVal;
        }
      });
    });
  }
}

customElements.define("price-range-slider", PriceRangeSlider);

class CustomFilter extends HTMLElement {
  static isGlobalListenerAttached = false;

  constructor() {
    super();
    this.control = this.querySelector(".filter-heading");
    this.body = this.querySelector(".filter-body");
  }

  connectedCallback() {
    if (!this.control || !this.body) {
      console.warn("CustomFilter: Missing required elements (.filter-heading or .filter-body)");
      return;
    }

    this.attachEventListeners();

    // Attach global click listener only once
    if (!CustomFilter.isGlobalListenerAttached) {
      CustomFilter.attachGlobalClickListener();
      CustomFilter.isGlobalListenerAttached = true;
    }
  }

  attachEventListeners() {
    this.control.addEventListener("click", () => {
      if (this.classList.contains("open")) {
        this.classList.remove("open");
      } else {
        // Close all other custom filters
        this.closeAllOtherFilters();
        // Open this filter
        this.classList.add("open");
      }
    });
  }

  closeAllOtherFilters() {
    const allCustomFilters = document.querySelectorAll("custom-filter");
    allCustomFilters.forEach((filter) => {
      if (filter !== this) {
        filter.classList.remove("open");
      }
    });
  }

  static attachGlobalClickListener() {
    document.body.addEventListener("click", (e) => {
      if (!e.target.closest(".custom-filter") && !e.target.closest(".filter-drawer")) {
        const allCustomFilters = document.querySelectorAll("custom-filter");
        allCustomFilters.forEach((filter) => {
          filter.classList.remove("open");
        });
      }
    });
  }

  disconnectedCallback() {
    if (this.control) {
      this.control.removeEventListener("click", this.handleClick);
    }
  }
}

customElements.define("custom-filter", CustomFilter);

class Filters extends HTMLElement {
  constructor() {
    super();
    this.mobileControl = this.querySelector(".mobile-filter-heading");
    this.filterBody = this.querySelector(".custom-filter-wrapper");
    this.filterInputs = this.querySelectorAll(
      ".filter-container-wrapper select, .filter-container-wrapper input",
    );
    this.filterForm = this.querySelector(".filter-container");
    const filterWrapper = this.closest(".filter-wrapper");
    this.resetFilters = (filterWrapper || this).querySelectorAll("[data-reset-filters]");
    this.selectedFiltersHtml = this.querySelectorAll(
      "[selected-filter], .selected-filter[data-param-remove]",
    );
    this.collectionGrid = document.querySelector("[data-filter-grid]");
  }

  /** Inner grid container (`[grid]`) used for pending visual state on filter fetch. */
  getResultsOverlayMount() {
    if (!this.collectionGrid) return null;
    const scoped =
      this.collectionGrid.querySelector(".collection-grid.grid") ||
      this.collectionGrid.querySelector(".product-grid[grid]");
    return scoped || this.collectionGrid;
  }

  /**
   * Mount for filter-driven View Transitions only (not the outer `[data-filter-grid]` section).
   * A non-none `view-transition-name` on the section creates a stacking context, so the mobile
   * fixed filter shell no longer stacks above later page sections.
   */
  getFilterViewTransitionTarget() {
    if (!this.collectionGrid) return null;
    return (
      this.collectionGrid.querySelector(".collection-grid.grid") ||
      this.collectionGrid.querySelector(".product-grid[grid]") ||
      this.collectionGrid.querySelector("[grid]")
    );
  }

  /** Named transition on the product grid only; never on the section root. */
  setFilterGridViewTransitionName() {
    if (!this.collectionGrid) return;
    this.collectionGrid.style.removeProperty("view-transition-name");
    const target = this.getFilterViewTransitionTarget();
    if (target) {
      if (this.prefersFilterViewTransition()) {
        target.style.viewTransitionName = FILTER_GRID_VIEW_TRANSITION_NAME;
      } else {
        target.style.removeProperty("view-transition-name");
      }
    }
  }

  /** Wrapper around filter UI for loading pointer-events. */
  getFilterPointerRoot() {
    return this.closest(".vertical-filters, .horizontal-filters, .filter-wrapper") || this;
  }

  connectedCallback() {
    this.attachMobileFilterListeners();
    this.attachFormSubmissionListeners();
    this.attachSelectedFilterListeners();
    this.attachResetFilterListener();
    this.initFilterDrawer();
    this.bindDrawerOffsetUpdates();
    this.initMobileGridToggle();
  }

  initFilterDrawer() {
    const drawer = document.querySelector(".filter-drawer");
    const overlay = document.querySelector(".filter-drawer-overlay");
    const toggles = document.querySelectorAll("[data-toggle-filter-drawer]");
    const closes = document.querySelectorAll("[data-close-filter-drawer]");

    if (drawer && overlay) {
      toggles.forEach((toggle) => {
        toggle.addEventListener("click", () => {
          drawer.classList.add("open");
          overlay.classList.add("open");
          this.lockPageScroll();
          const headerGroup = document.querySelector(".header-group");
          if (headerGroup) headerGroup.classList.add("active");
        });
      });

      closes.forEach((close) => {
        close.addEventListener("click", () => this.closeFilterDrawer());
      });
    }
  }

  lockPageScroll() {
    this.updateDrawerOffset();
    document.body.classList.add("filter-drawer-open");
  }

  updateDrawerOffset() {
    const header = document.querySelector("#header-group") || document.querySelector(".header-group");
    if (!header) return;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty("--filter-drawer-offset", `${bottom}px`);
  }

  bindDrawerOffsetUpdates() {
    if (this.drawerOffsetBound) return;
    this.drawerOffsetBound = true;
    const update = () => {
      if (document.body.classList.contains("filter-drawer-open")) this.updateDrawerOffset();
    };
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
  }

  unlockPageScroll() {
    document.body.classList.remove("filter-drawer-open");
  }

  initMobileGridToggle() {
    const grid = document.querySelector("[data-filter-grid] [grid]");
    const toggle1 = document.querySelector('[data-columns="1"]');
    const toggle2 = document.querySelector('[data-columns="2"]');

    if (grid && toggle1 && toggle2) {
      const savedCols = localStorage.getItem("collection-grid-cols") || "1";
      setCols.call(this, savedCols);

      toggle1.addEventListener("click", () => setColsKeepingPosition.call(this, "1"));
      toggle2.addEventListener("click", () => setColsKeepingPosition.call(this, "2"));

      // Switching columns changes the grid height, so keep the product the user
      // was looking at in the same spot on screen rather than leaving them stranded.
      function setColsKeepingPosition(cols) {
        if (grid.getAttribute("data-columns-mobile") === cols) return;

        const bar = this.closest(".filter-wrapper");
        const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
        const anchor = Array.from(grid.children).find((item) => item.getBoundingClientRect().bottom > barBottom);
        const anchorTop = anchor ? anchor.getBoundingClientRect().top : null;

        setCols.call(this, cols);

        if (anchor && anchorTop !== null && anchorTop < window.innerHeight) {
          const delta = anchor.getBoundingClientRect().top - anchorTop;
          if (delta) window.scrollTo({ top: window.scrollY + delta, behavior: "instant" });
        }
      }

      function setCols(cols) {
        grid.setAttribute("data-columns-mobile", cols);
        const filterGrid = grid.closest("[data-filter-grid]");
        if (filterGrid) {
          filterGrid.setAttribute("data-mobile-cols", cols);
        }

        if (cols === "1") {
          grid.style.setProperty("--grid-columns-mobile", "1fr");
          grid.style.setProperty("--grid-columns-span-mobile", "1");
          toggle1.style.opacity = "1";
          toggle2.style.opacity = "0.5";
          toggle1.classList.add("active");
          toggle2.classList.remove("active");
          toggle1.setAttribute("aria-pressed", "true");
          toggle2.setAttribute("aria-pressed", "false");
        } else {
          grid.style.setProperty("--grid-columns-mobile", "repeat(2, minmax(0, 1fr))");
          grid.style.setProperty("--grid-columns-span-mobile", "2");
          toggle1.style.opacity = "0.5";
          toggle2.style.opacity = "1";
          toggle1.classList.remove("active");
          toggle2.classList.add("active");
          toggle1.setAttribute("aria-pressed", "false");
          toggle2.setAttribute("aria-pressed", "true");
        }
        localStorage.setItem("collection-grid-cols", cols);
      }
    }
  }

  closeFilterDrawer() {
    const drawer = document.querySelector(".filter-drawer");
    const overlay = document.querySelector(".filter-drawer-overlay");
    if (drawer && overlay) {
      drawer.classList.remove("open");
      overlay.classList.remove("open");
    }
    this.unlockPageScroll();
  }

  isMobileOrTabletViewport() {
    return window.matchMedia("(max-width: 991.98px)").matches;
  }

  getCollectionGridScrollTop() {
    const grid = this.collectionGrid || document.querySelector("[data-filter-grid]");
    if (!grid) return 0;
    const target = this.getResultsOverlayMount() || grid;
    const rect = target.getBoundingClientRect();
    const bar = this.closest(".filter-wrapper") || document.querySelector(".filter-wrapper");
    let offset = 180;
    if (bar) {
      const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
      offset = stickyTop + bar.offsetHeight + 12;
    }
    return Math.max(0, Math.round(window.scrollY + rect.top - offset));
  }

  prefersFilterViewTransition() {
    // Disabled View Transitions on filter updates: document.startViewTransition captures
    // the sticky .filter-wrapper in the root transition layer, causing the filter toolbar
    // to fade out and then reshow while the collection grid is updating.
    return false;
  }

  attachMobileFilterListeners() {
    if (this.mobileControl && this.filterBody) {
      this.mobileControl.addEventListener("click", () => {
        // Toggle mobile control open class
        if (this.mobileControl.classList.contains("open")) {
          this.mobileControl.classList.remove("open");
        } else {
          this.mobileControl.classList.add("open");
        }

        // Toggle filter body open class
        if (this.filterBody.classList.contains("open")) {
          this.filterBody.classList.remove("open");
        } else {
          this.filterBody.classList.add("open");
        }
      });
    }
  }

  attachFormSubmissionListeners() {
    if (this.filterInputs && this.filterForm && this.collectionGrid) {
      // Get the apply button you added in Liquid
      const applyBtn = this.filterForm.querySelector(".apply-filters-btn");

      this.filterInputs.forEach((input) => {
        // If an input is inside the drawer, do not auto-submit on change;
        // let the user batch their selections and click "Apply Filters".
        const isInDrawer = Boolean(input.closest(".filter-drawer"));
        if (!isInDrawer) {
          input.addEventListener("change", () => {
            this.handleFilterChange(false);
          });
        }
      });

      this.filterForm.addEventListener("submit", (event) => {
        event.preventDefault();
        this.handleFilterChange(true);
      });

      if (applyBtn) {
        applyBtn.addEventListener("click", (event) => {
          event.preventDefault();
          this.handleFilterChange(true);
        });
      }
    }
  }

  async handleFilterChange(shouldCloseDrawer = false) {
    if (!this.filterForm || !this.collectionGrid) {
      return;
    }

    // Close mobile filter drawer if explicitly requested (e.g. from apply button or submit)
    if (shouldCloseDrawer) {
      this.closeFilterDrawer();
    }

    // Clear stored load more count since we're filtering
    this.clearLoadMoreState();

    // Show loading state
    this.showLoadingState();

    try {
      // Get form data manually instead of using FormData (which can be unreliable)

      const params = new URLSearchParams();

      // Get all form elements manually
      const formElements = this.filterForm.querySelectorAll("input, select, textarea");

      formElements.forEach((element, index) => {
        const name = element.name;
        let value = element.value;
        let shouldInclude = false;

        // Determine if this element should be included
        if (!name || name.trim() === "") {
          return;
        }

        switch (element.type) {
          case "checkbox":
            shouldInclude = element.checked;
            break;
          case "radio":
            shouldInclude = element.checked;
            break;
          case "select-one":
          case "select-multiple":
            // For select elements, check if a non-default option is selected
            shouldInclude = value && value.trim() !== "";
            break;
          default:
            // For text, number, range, etc.
            shouldInclude = value && value.trim() !== "";
            break;
        }

        if (shouldInclude) {
          params.append(name, value);
        }
      });

      // Build section request URL from the listing this filter belongs to
      const currentUrl = this.qflBase();

      // Clear existing filter/search/sort params before rebuilding from form state.
      // We remove all `filter.*` keys dynamically so custom metafield filters are handled too.
      const keysToDelete = [];
      for (const [paramKey] of currentUrl.searchParams) {
        if (
          paramKey === "sort_by" ||
          paramKey === "type" ||
          paramKey === "q" ||
          paramKey === "page" ||
          paramKey.startsWith("filter.")
        ) {
          keysToDelete.push(paramKey);
        }
      }
      keysToDelete.forEach((key) => {
        currentUrl.searchParams.delete(key);
      });

      // Add new filter parameters to the request URL
      for (const [key, value] of params) {
        if (value && value.trim() !== "") {
          currentUrl.searchParams.append(key, value);
        }
      }

      // Add section parameter for Shopify section rendering
      const sectionName = this.getSectionName();
      if (sectionName) {
        currentUrl.searchParams.set("sections", sectionName);
      }

      // Check if we're in local development environment
      const isLocalDev =
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "localhost" ||
        window.location.port !== "";

      // Try section rendering first (skip in local dev as it often fails)
      let sectionsWorked = false;
      if (sectionName && !isLocalDev) {
        try {
          const response = await fetch(currentUrl.toString(), {
            method: "GET",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/json",
            },
            credentials: "same-origin",
          });

          if (response.ok) {
            const data = await response.json();

            if (data && data[sectionName]) {
              this.updateCollectionGrid(data[sectionName]);
              this.updateBrowserUrl(params);
              sectionsWorked = true;
            }
          } else {
            await response.text();
          }
        } catch {
          /* Section render request failed */
        }
      }

      // If section rendering didn't work, fall back to full page request
      if (!sectionsWorked) {
        // Remove sections parameter for full page request
        const fullPageUrl = new URL(currentUrl);
        fullPageUrl.searchParams.delete("sections");

        const fullResponse = await fetch(fullPageUrl.toString(), {
          method: "GET",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (!fullResponse.ok) {
          console.error(`Full page request failed! status: ${fullResponse.status}`);
          throw new Error(`HTTP error! status: ${fullResponse.status}`);
        }

        const html = await fullResponse.text();
        this.updateCollectionGrid(html);
        this.updateBrowserUrl(params);
      }
    } catch (error) {
      console.error("Filter request failed:", error);
      this.hideLoadingState();
    }
  }

  updateBrowserUrl(params) {
    // Borrowed listing: the address bar belongs to the host page.
    if (this.qflDetached()) return;
    // Update URL without page reload (clean URL without sections parameter)
    const newUrl = new URL(window.location.href);

    // Clear existing filter/search/sort/page parameters before rebuilding from form state.
    const keysToDelete = [];
    for (const [paramKey] of newUrl.searchParams) {
      if (
        paramKey === "sort_by" ||
        paramKey === "type" ||
        paramKey === "q" ||
        paramKey === "page" ||
        paramKey.startsWith("filter.")
      ) {
        keysToDelete.push(paramKey);
      }
    }
    keysToDelete.forEach((key) => {
      newUrl.searchParams.delete(key);
    });

    // Remove sections parameter (used for API calls only)
    newUrl.searchParams.delete("sections");

    // Add new filter parameters
    for (const [key, value] of params) {
      if (value && value.trim() !== "") {
        newUrl.searchParams.append(key, value);
      }
    }

    // Update the browser URL
    window.history.pushState({}, "", newUrl.toString());
  }

  clearLoadMoreState() {
    // Clear localStorage for any grid-load-more elements on the page
    try {
      const gridElements = document.querySelectorAll("grid-load-more");
      gridElements.forEach((element) => {
        if (typeof element.clearStoredCount === "function") {
          element.clearStoredCount();
        }
      });
    } catch (error) {
      console.warn("Failed to clear load more state:", error);
    }
  }

  showLoadingState() {
    const mount = this.getResultsOverlayMount();
    if (mount) {
      mount.classList.add(FILTER_RESULTS_PENDING_CLASS);
      mount.querySelector(".filter-loading-overlay")?.remove();
    }

    this.setFormInputsDisabled(true);

    if (this.collectionGrid) {
      this.collectionGrid.style.pointerEvents = "none";
    }

    const applyBtn = this.filterForm?.querySelector(".apply-filters-btn");
    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.setAttribute("aria-busy", "true");
    }
  }

  hideLoadingState() {
    const mount = this.getResultsOverlayMount();
    if (mount) {
      mount.classList.remove(FILTER_RESULTS_PENDING_CLASS);
      mount.querySelector(".filter-loading-overlay")?.remove();
    }

    this.setFormInputsDisabled(false);

    if (this.collectionGrid) {
      this.collectionGrid.style.pointerEvents = "";
    }

    const applyBtn = this.filterForm?.querySelector(".apply-filters-btn");
    if (applyBtn) {
      applyBtn.disabled = false;
      applyBtn.removeAttribute("aria-busy");
    }
  }

  setFormInputsDisabled(disabled) {
    if (this.filterInputs) {
      this.filterInputs.forEach((input) => {
        input.disabled = disabled;
      });
    }
  }

  // ── Detached listings ────────────────────────────────────────────
  // Every request below used to be built from window.location.href, which
  // is right when this filter set is ON the collection page and wrong the
  // moment the listing is rendered somewhere else. Enlist fetches this
  // section into its slideout, so "here" was /pages/locker-room and every
  // filter fetched a page with no collection on it — the grid never
  // changed and nothing said why.
  //
  // A surface that has borrowed a listing names the URL it borrowed by
  // putting data-qfl-collection-base on an ancestor. Nothing else changes:
  // with no such ancestor these return the page, exactly as before.
  qflBase() {
    const host = this.closest("[data-qfl-collection-base]");
    // Remembered, because updateCollectionGrid replaces the markup this
    // element sits in — so by the time updateBrowserUrl runs, `this` is
    // detached and closest() finds nothing. Without the cache the guard
    // below read "not detached" and the filter rewrote the address bar of
    // the page the slideout was sitting on.
    if (host) this._qflBaseHref = host.getAttribute("data-qfl-collection-base");
    return new URL(this._qflBaseHref || window.location.href, window.location.origin);
  }

  // Detached filters must not touch the address bar: the shopper is still
  // on the locker room, and rewriting the URL to the collection would make
  // Back leave the page they are working in.
  qflDetached() {
    return !!(this.closest("[data-qfl-collection-base]") || this._qflBaseHref);
  }

  getSectionName() {
    // Method 1: Find section by data-filter-grid element
    const sectionElement = document.querySelector("[data-filter-grid]");

    if (sectionElement) {
      const section = sectionElement.closest('[id^="shopify-section-"]');

      if (section && section.id) {
        // Remove 'shopify-section-' prefix if present
        const sectionId = section.id.replace(/^shopify-section-/, "");
        return sectionId;
      }
    }

    // Method 2: Check URL patterns — of the listing's own URL, so a
    // borrowed collection still resolves to the collection section rather
    // than to whatever page it is being shown on.
    const pathname = this.qflBase().pathname;

    if (pathname.includes("/search")) {
      return "template--search";
    } else if (pathname.includes("/collections/")) {
      return "template--collection";
    }

    // Method 3: Try common section names by searching DOM
    const possibleSections = ["template--collection", "template--search", "collection", "search"];

    for (const sectionName of possibleSections) {
      const variations = [
        `#${sectionName}`,
        `#shopify-section-${sectionName}`,
        `section[id="${sectionName}"]`,
        `section[id="shopify-section-${sectionName}"]`,
      ];

      for (const selector of variations) {
        const element = document.querySelector(selector);
        if (element) {
          return sectionName;
        }
      }
    }

    console.warn("❌ Could not determine section name");
    return null;
  }

  /**
   * Replace grid markup (shared by string / section HTML paths).
   * Sets view-transition-name on the inner `[grid]` mount only (see `setFilterGridViewTransitionName`).
   */
  applyCollectionGridMarkup(html) {
    // Save drawer open states and scroll position before replacing the DOM
    const drawer = document.querySelector(".filter-drawer");
    const overlay = document.querySelector(".filter-drawer-overlay");
    const isDrawerOpen = drawer && drawer.classList.contains("open");
    const isOverlayOpen = overlay && overlay.classList.contains("open");
    const isBodyOpen = document.body.classList.contains("filter-drawer-open");

    const drawerBody = document.querySelector(".filter-drawer__body");
    const drawerScrollTop = drawerBody ? drawerBody.scrollTop : 0;

    // Save open filter categories by their label text to avoid indexing mismatch after DOM replacement
    const openFilterLabels = [];
    const allFilters = document.querySelectorAll("custom-filter");
    allFilters.forEach((filter) => {
      if (filter.classList.contains("open")) {
        const heading = filter.querySelector(".filter-heading");
        if (heading) {
          openFilterLabels.push(heading.textContent.trim().toLowerCase());
        }
      }
    });

    if (typeof html === "string") {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newSection =
        doc.querySelector("section[data-filter-grid]") ||
        doc.querySelector("[data-filter-grid]") ||
        doc.querySelector('section[id*="collection"]') ||
        doc.querySelector('section[id*="search"]');

      if (newSection && this.collectionGrid) {
        const parent = this.collectionGrid.parentElement;
        this.collectionGrid.outerHTML = newSection.outerHTML;
        this.collectionGrid =
          parent?.querySelector("[data-filter-grid]") ||
          document.querySelector("[data-filter-grid]") ||
          parent?.querySelector('section[id*="collection"]') ||
          document.querySelector('section[id*="collection"]');
      } else if (this.collectionGrid) {
        console.warn(
          "Could not find data-filter-grid element in new HTML, using innerHTML fallback",
        );
        this.collectionGrid.innerHTML = html;
      }

      // Update the filter container (applied filter chips, counts, reset button, and drawer options)
      const newFilterContainer = doc.querySelector("filter-container");
      if (newFilterContainer) {
        this.innerHTML = newFilterContainer.innerHTML;
        this.rebindFilterContainer();
      } else {
        // Fallback: update individual applied filter bars if entire container is not in response
        const newAppliedList = doc.querySelectorAll(".filter-bar__applied-filters");
        const currentAppliedList = document.querySelectorAll(".filter-bar__applied-filters");
        if (newAppliedList.length && currentAppliedList.length) {
          currentAppliedList.forEach((el, index) => {
            if (newAppliedList[index]) {
              el.innerHTML = newAppliedList[index].innerHTML;
            }
          });
          this.attachSelectedFilterListeners();
        }

        const newResults = doc.querySelector(".filter-bar__results");
        const currentResults = document.querySelector(".filter-bar__results");
        if (newResults && currentResults) {
          currentResults.innerHTML = newResults.innerHTML;
        }

        const newReset = doc.querySelectorAll("[data-reset-filters]");
        const currentReset = document.querySelectorAll("[data-reset-filters]");
        if (newReset.length !== currentReset.length) {
          this.rebindFilterContainer();
        }
      }
    } else if (this.collectionGrid) {
      this.collectionGrid.innerHTML = html;
    }

    // Restore drawer open states and scroll position
    if (isDrawerOpen || isOverlayOpen) {
      const newDrawer = document.querySelector(".filter-drawer");
      const newOverlay = document.querySelector(".filter-drawer-overlay");
      if (newDrawer && isDrawerOpen) newDrawer.classList.add("open");
      if (newOverlay) newOverlay.classList.add("open");
      if (isBodyOpen) document.body.classList.add("filter-drawer-open");
    }

    if (drawerScrollTop > 0) {
      const newDrawerBody = document.querySelector(".filter-drawer__body");
      if (newDrawerBody) {
        newDrawerBody.scrollTop = drawerScrollTop;
      }
    }

    // Restore expanded accordions by matching heading labels
    const newFilters = (this || document).querySelectorAll("custom-filter");
    newFilters.forEach((filter) => {
      const heading = filter.querySelector(".filter-heading");
      if (heading) {
        const label = heading.textContent.trim().toLowerCase();
        if (openFilterLabels.includes(label)) {
          filter.classList.add("open");
        }
      }
    });

    this.setFilterGridViewTransitionName();

    this.reinitializeComponents();
  }

  updateCollectionGrid(html) {
    if (!html || !this.collectionGrid) {
      console.warn("updateCollectionGrid: Missing html or collectionGrid", {
        html: !!html,
        collectionGrid: !!this.collectionGrid,
      });
      this.hideLoadingState();
      return;
    }

    const scheduleRestoreScroll = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const targetTop = this.getCollectionGridScrollTop();
          window.scrollTo({ top: targetTop, left: 0, behavior: "smooth" });
        });
      });
    };

    const useViewTransition = this.prefersFilterViewTransition();

    const run = () => {
      try {
        this.applyCollectionGridMarkup(html);
      } catch (error) {
        console.error("Failed to update collection grid:", error);
      } finally {
        this.hideLoadingState();
        if (!useViewTransition) {
          scheduleRestoreScroll();
        }
      }
    };

    if (useViewTransition) {
      this.setFilterGridViewTransitionName();
      document.documentElement.classList.add("filter-results-vt");
      try {
        const vt = document.startViewTransition(run);
        vt.finished.finally(() => {
          document.documentElement.classList.remove("filter-results-vt");
          scheduleRestoreScroll();
        });
      } catch (e) {
        document.documentElement.classList.remove("filter-results-vt");
        run();
        scheduleRestoreScroll();
      }
    } else {
      run();
    }
  }

  reinitializeComponents() {
    try {
      if (!this.collectionGrid) {
        console.warn("Cannot reinitialize: collectionGrid is null");
        return;
      }

      // Re-initialize any grid-load-more elements in the updated section
      const gridLoadMoreElements = this.collectionGrid.querySelectorAll("grid-load-more");

      gridLoadMoreElements.forEach((element, index) => {
        try {
          // Force re-initialization if not already connected
          if (!element.grid || !element.loadMoreBtn) {
            // Trigger connectedCallback manually
            if (typeof element.connectedCallback === "function") {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(`Failed to reinitialize grid-load-more element ${index + 1}:`, elemError);
        }
      });

      // Re-initialize price range sliders
      const priceRangeElements = (this || document).querySelectorAll("price-range-slider");

      priceRangeElements.forEach((element, index) => {
        try {
          if (!element.rangeInputs || element.rangeInputs.length === 0) {
            if (typeof element.connectedCallback === "function") {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(
            `Failed to reinitialize price-range-slider element ${index + 1}:`,
            elemError,
          );
        }
      });

      // Re-initialize any custom-filter elements
      const customFilterElements = (this || document).querySelectorAll("custom-filter");

      customFilterElements.forEach((element, index) => {
        try {
          if (!element.control || !element.body) {
            element.control = element.querySelector(".filter-heading");
            element.body = element.querySelector(".filter-body");
            if (typeof element.connectedCallback === "function") {
              element.connectedCallback();
            }
          }
        } catch (elemError) {
          console.warn(`Failed to reinitialize custom-filter element ${index + 1}:`, elemError);
        }
      });
    } catch (error) {
      console.warn("❌ Failed to reinitialize components:", error);
    }
  }

  rebindFilterContainer() {
    this.mobileControl = this.querySelector(".mobile-filter-heading");
    this.filterBody = this.querySelector(".custom-filter-wrapper");
    this.filterInputs = this.querySelectorAll(
      ".filter-container-wrapper select, .filter-container-wrapper input",
    );
    this.filterForm = this.querySelector(".filter-container");
    const filterWrapper = this.closest(".filter-wrapper");
    this.resetFilters = (filterWrapper || this).querySelectorAll("[data-reset-filters]");
    this.selectedFiltersHtml = this.querySelectorAll(
      "[selected-filter], .selected-filter[data-param-remove]",
    );

    this.attachMobileFilterListeners();
    this.attachFormSubmissionListeners();
    this.attachSelectedFilterListeners();
    this.attachResetFilterListener();
    this.initFilterDrawer();
    this.initMobileGridToggle();
  }

  attachSelectedFilterListeners() {
    if (!this.collectionGrid) {
      return;
    }

    const selectedFilterButtons = document.querySelectorAll(
      "[selected-filter], .selected-filter[data-param-remove]",
    );

    selectedFilterButtons.forEach((selected) => {
      if (selected.dataset.selectedFilterBound === "true") {
        return;
      }

      selected.dataset.selectedFilterBound = "true";
      selected.addEventListener("click", (event) => {
        this.handleSelectedFilterRemoval(event);
      });
    });
  }

  attachResetFilterListener() {
    if (!this.resetFilters.length || !this.collectionGrid) return;

    this.resetFilters.forEach((resetButton) => {
      if (resetButton.dataset.resetFiltersBound === "true") return;
      resetButton.dataset.resetFiltersBound = "true";
      resetButton.addEventListener("click", (event) => {
        event.preventDefault();
        this.handleResetFilters();
      });
    });
  }

  async handleResetFilters() {
    if (!this.filterForm) return;

    const resettableFields = this.filterForm.querySelectorAll("input, select, textarea");

    resettableFields.forEach((field) => {
      const name = field.name || "";
      const shouldKeepField = name === "sort_by" || name === "q" || name === "type";

      if (shouldKeepField || field.disabled) return;

      switch (field.type) {
        case "checkbox":
        case "radio":
          field.checked = false;
          break;
        case "select-one":
          field.selectedIndex = 0;
          break;
        case "select-multiple":
          Array.from(field.options).forEach((option) => {
            option.selected = false;
          });
          break;
        default:
          field.value = "";
      }
    });

    await this.handleFilterChange();
  }

  async handleSelectedFilterRemoval(event) {
    event.preventDefault();

    const selectedFilterButton = event.currentTarget;
    const paramRemoveString = selectedFilterButton?.getAttribute("data-param-remove");

    if (!paramRemoveString) {
      return;
    }

    this.clearLoadMoreState();
    this.showLoadingState();

    try {
      const currentUrl = this.qflBase();
      const paramsToRemove = new URLSearchParams(paramRemoveString);

      paramsToRemove.forEach((value, key) => {
        this.removeSearchParamByValue(currentUrl.searchParams, key, value);
      });

      // Ensure we return to the first page after removing filters.
      currentUrl.searchParams.delete("page");

      await this.fetchAndRenderFromUrl(currentUrl);

      currentUrl.searchParams.delete("sections");
      window.history.pushState({}, "", currentUrl.toString());
    } catch (error) {
      console.error("Failed to remove selected filter:", error);
      this.hideLoadingState();
    }
  }

  removeSearchParamByValue(searchParams, key, valueToRemove) {
    if (key === "filter.v.price.gte" || key === "filter.v.price.lte") {
      searchParams.delete(key);
      return;
    }

    const existingValues = searchParams.getAll(key);

    if (!existingValues.length) {
      return;
    }

    searchParams.delete(key);

    if (!valueToRemove || valueToRemove === "") {
      return;
    }

    existingValues.forEach((value) => {
      if (value !== valueToRemove) {
        searchParams.append(key, value);
      }
    });
  }

  async fetchAndRenderFromUrl(requestUrl) {
    const sectionName = this.getSectionName();
    const requestWithSection = new URL(requestUrl.toString());

    if (sectionName) {
      requestWithSection.searchParams.set("sections", sectionName);
    }

    const isLocalDev =
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost" ||
      window.location.port !== "";

    let sectionsWorked = false;

    if (sectionName && !isLocalDev) {
      try {
        const response = await fetch(requestWithSection.toString(), {
          method: "GET",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          credentials: "same-origin",
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data[sectionName]) {
            this.updateCollectionGrid(data[sectionName]);
            sectionsWorked = true;
          }
        }
      } catch (error) {
        console.warn("Section render request failed during selected filter removal:", error);
      }
    }

    if (!sectionsWorked) {
      const fullPageUrl = new URL(requestWithSection.toString());
      fullPageUrl.searchParams.delete("sections");

      const fullResponse = await fetch(fullPageUrl.toString(), {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!fullResponse.ok) {
        throw new Error(`HTTP error! status: ${fullResponse.status}`);
      }

      const html = await fullResponse.text();
      this.updateCollectionGrid(html);
    }
  }
}

customElements.define("filter-container", Filters);
